'use client';

import { Icon } from '@iconify/react';
import { type TodoProject } from '@prisma/client';
import {
  AlertCircle,
  CalendarIcon,
  Check,
  CheckCircle2,
  ChevronRight,
  Hash,
  Inbox,
  MoreHorizontal,
  Plus,
  Sun,
} from 'lucide-react';
import Image from 'next/image';
import React, { memo, useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Input } from '../../../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

type UserInfo = { name?: string | null; image?: string | null };

export const mainFilters = [
  { id: 'inbox', label: 'Входящие', icon: Inbox },
  { id: 'today', label: 'Сегодня', icon: Sun },
  { id: 'upcoming', label: 'Предстоящее', icon: ChevronRight },
  { id: 'overdue', label: 'Просрочено', icon: AlertCircle },
  { id: 'completed', label: 'Выполнено', icon: CheckCircle2 },
] as const;

export type MainFilterId = (typeof mainFilters)[number]['id'];
export type SidebarCounts = Record<MainFilterId, number>;
export type ProjectCounts = Record<string, number>;

interface SidebarProps {
  user: UserInfo;
  projects: TodoProject[];

  // ✅ вместо tasks[]
  counts: SidebarCounts;
  projectCounts: ProjectCounts;

  activeFilter: string;
  setActiveFilter: (filter: string) => void;

  onAddTaskClick: () => void;
  onProjectCreated: (name: string) => Promise<TodoProject | void>;
  onProjectUpdated: (id: string, name: string) => Promise<TodoProject | void>;
  onProjectDeleted: (id: string) => Promise<void>;

  isMobile?: boolean;
  onClose?: () => void;
}

function getErrorMessage(err: unknown, fallback = 'Ошибка.') {
  return err instanceof Error ? err.message : fallback;
}

interface DateIconProps {
  size?: number;
  className?: string;
  circleColor?: string;
  calendarColor?: string;
}
export const DateIcon = ({
  size = 18,
  className = '',
  circleColor = 'bg-blue-500',
  calendarColor = 'text-blue-600',
}: DateIconProps) => {
  const day = new Date().getDate();
  const textSize = Math.max(10, size * 0.55);

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      <CalendarIcon size={size} className={cn(calendarColor, 'opacity-80')} />
      <div
        className={cn(
          'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center',
          circleColor,
        )}
        style={{ width: size * 0.7, height: size * 0.7 }}
      >
        <span
          className="text-white font-bold leading-none text-center"
          style={{ fontSize: textSize }}
        >
          {day}
        </span>
      </div>
    </div>
  );
};

const projectIconCache = new Map<string, React.ReactNode>();
export const getProjectIcon = (projectName: string, iconSize: number = 18) => {
  const name = projectName.toLowerCase();
  const size = iconSize;

  if (name === 'сегодня') {
    return <DateIcon size={size} circleColor="bg-blue-500" calendarColor="text-blue-600" />;
  }

  const cacheKey = `${size}|${name}`;
  const cached = projectIconCache.get(cacheKey);
  if (cached) return cached;

  let node: React.ReactNode = <Hash size={size} className="text-slate-400" />;

  if (name.includes('семья'))
    node = <Icon icon="noto-v1:family-man-woman-girl" width={size} height={size} />;
  else if (name.includes('работа'))
    node = <Icon icon="twemoji:briefcase" width={size} className="text-indigo-500" />;
  else if (name.includes('отдых') || name.includes('хобби'))
    node = <Icon icon="twemoji:palm-tree" width={size} className="text-emerald-500" />;
  else if (name.includes('дом')) node = <Icon icon="twemoji:house" width={size} height={size} />;
  else if (name === 'входящие')
    node = <Icon icon="twemoji:inbox-tray" width={size} className="text-sky-500" />;
  else if (name === 'предстоящее')
    node = <Icon icon="twemoji:calendar" width={size} className="text-blue-400" />;
  else if (name === 'просрочено')
    node = <Icon icon="twemoji:alarm-clock" width={size} className="text-red-500" />;
  else if (name === 'выполнено')
    node = <Icon icon="twemoji:white-heavy-check-mark" width={size} className="text-emerald-500" />;

  projectIconCache.set(cacheKey, node);
  return node;
};

const FilterButton = memo(function FilterButton({
  id,
  label,
  IconComp,
  isActive,
  count,
  onClick,
}: {
  id: MainFilterId;
  label: string;
  IconComp: React.ComponentType<{ size?: number }>;
  isActive: boolean;
  count: number;
  onClick: (id: MainFilterId) => void;
}) {
  const handleClick = useCallback(() => onClick(id), [id, onClick]);

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-center gap-3 p-2 rounded-md text-sm transition-colors text-left w-full',
        isActive
          ? 'bg-sky-600/30 text-white font-semibold'
          : 'text-slate-300 hover:bg-slate-800/60',
      )}
      aria-current={isActive}
      type="button"
    >
      <IconComp size={18} />
      <span>{label}</span>
      {count > 0 && (
        <span className="ml-auto text-xs font-semibold bg-slate-700 px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </button>
  );
});

const ProjectRow = memo(function ProjectRow({
  project,
  isActive,
  count,
  isEditing,
  editValue,
  onEditValueChange,
  onSelect,
  onStartEdit,
  onSave,
  onDelete,
}: {
  project: TodoProject;
  isActive: boolean;
  count: number;

  isEditing: boolean;
  editValue: string;
  onEditValueChange: (v: string) => void;

  onSelect: (projectId: string) => void;
  onStartEdit: (project: TodoProject) => void;
  onSave: () => void;
  onDelete: (projectId: string) => void;
}) {
  const handleSelect = useCallback(() => onSelect(project.id), [onSelect, project.id]);
  const handleStartEdit = useCallback(() => onStartEdit(project), [onStartEdit, project]);
  const handleDelete = useCallback(() => onDelete(project.id), [onDelete, project.id]);

  return (
    <li
      className={cn(
        'flex items-center group w-full p-2 rounded-md transition-colors relative',
        isActive ? 'bg-sky-600/30' : 'hover:bg-slate-800/60',
      )}
    >
      {isEditing ? (
        <div className="flex items-center gap-2 w-full">
          <Input
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onBlur={onSave}
            onKeyDown={(e) => e.key === 'Enter' && onSave()}
            autoFocus
            className="h-7 text-sm bg-slate-900 border-slate-700 text-white"
          />
        </div>
      ) : (
        <>
          <button
            onClick={handleSelect}
            className="flex items-center gap-3 text-sm text-left flex-grow min-w-0"
            aria-current={isActive}
            type="button"
          >
            {getProjectIcon(project.name)}
            <span className="text-slate-300 truncate">{project.name}</span>
          </button>

          <div className="flex items-center ml-auto pl-2">
            {count > 0 && (
              <span
                className={cn(
                  'text-xs font-semibold text-slate-400 px-1 py-0.5 rounded-full transition-opacity duration-200',
                  'group-hover:opacity-0',
                )}
              >
                {count}
              </span>
            )}

            <div
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100',
                'transition-opacity duration-200',
              )}
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="p-1 rounded-md hover:bg-slate-700"
                    aria-label="Меню проекта"
                  >
                    <MoreHorizontal size={16} className="text-slate-400" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                  <DropdownMenuItem onSelect={handleStartEdit}>Изменить</DropdownMenuItem>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem
                        onSelect={(e) => e.preventDefault()}
                        className="text-red-500"
                      >
                        Удалить
                      </DropdownMenuItem>
                    </AlertDialogTrigger>

                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Удалить проект?</AlertDialogTitle>
                        <AlertDialogDescription>
                          {`Все задачи в проекте "${project.name}" также будут удалены.`}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Отмена</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>Удалить</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </>
      )}
    </li>
  );
});

export function TodoSidebar({
  user,
  projects,
  counts,
  projectCounts,
  activeFilter,
  setActiveFilter,
  onAddTaskClick,
  onProjectCreated,
  onProjectUpdated,
  onProjectDeleted,
  isMobile = false,
  onClose,
}: SidebarProps) {
  const [editingProject, setEditingProject] = useState<TodoProject | null>(null);
  const [newProjectName, setNewProjectName] = useState('');

  const [isAddingProject, setIsAddingProject] = useState(false);
  const [addProjectName, setAddProjectName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFilterClick = useCallback(
    (filterId: string) => {
      setActiveFilter(filterId);
      onClose?.();
    },
    [setActiveFilter, onClose],
  );

  const handleAddTask = useCallback(() => {
    onAddTaskClick();
    onClose?.();
  }, [onAddTaskClick, onClose]);

  const handleStartEdit = useCallback((project: TodoProject) => {
    setIsAddingProject(false);
    setAddProjectName('');
    setEditingProject(project);
    setNewProjectName(project.name);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingProject) return;

    const next = newProjectName.trim();
    if (!next || next === editingProject.name) {
      setEditingProject(null);
      return;
    }

    try {
      await toast.promise(onProjectUpdated(editingProject.id, next), {
        loading: 'Сохранение...',
        success: 'Проект обновлён!',
        error: (err) => getErrorMessage(err, 'Не удалось обновить проект.'),
      });
    } finally {
      setEditingProject(null);
    }
  }, [editingProject, newProjectName, onProjectUpdated]);

  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      await toast.promise(onProjectDeleted(projectId), {
        loading: 'Удаление...',
        success: 'Проект удалён!',
        error: (err) => getErrorMessage(err, 'Не удалось удалить проект.'),
      });

      if (activeFilter === projectId) setActiveFilter('inbox');
    },
    [activeFilter, onProjectDeleted, setActiveFilter],
  );

  const startAddProject = useCallback(() => {
    setEditingProject(null);
    setNewProjectName('');
    setIsAddingProject(true);
  }, []);

  const cancelAddProject = useCallback(() => {
    setIsAddingProject(false);
    setAddProjectName('');
  }, []);

  const handleCreateProject = useCallback(async () => {
    if (isSubmitting) return;

    const name = addProjectName.trim();
    if (!name) {
      cancelAddProject();
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await onProjectCreated(name);
      if (created) {
        toast.success('Проект создан!');
        setAddProjectName('');
        setIsAddingProject(false);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Не удалось создать проект.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, addProjectName, onProjectCreated, cancelAddProject]);

  const handleSelectProject = useCallback(
    (projectId: string) => handleFilterClick(projectId),
    [handleFilterClick],
  );

  return (
    <TooltipProvider delayDuration={100}>
      <aside
        className={cn(
          'flex flex-col flex-shrink-0 bg-slate-800/50 p-4 rounded-lg',
          isMobile && 'h-full bg-slate-900 rounded-none',
        )}
        aria-label="Боковая панель задач"
      >
        <div className="flex items-center gap-3 mb-6">
          <Image
            src={user.image || '/default-avatar.jpg'}
            alt={user.name || 'Аватар'}
            width={32}
            height={32}
            className="rounded-full"
          />
          <span className="font-bold text-white">{user.name || 'Пользователь'}</span>
        </div>

        <button
          onClick={handleAddTask}
          className="flex items-center gap-2 p-2 text-red-400 font-semibold hover:bg-red-400/10 rounded-md transition-colors mb-4"
          aria-label="Добавить новую задачу"
          type="button"
        >
          <Plus size={20} className="bg-red-500 text-white rounded-full p-0.5" />
          <span>Добавить задачу</span>
        </button>

        <div className="flex-grow overflow-y-auto pr-2 space-y-6">
          <nav aria-label="Основные фильтры">
            <ul className="flex flex-col gap-1">
              {mainFilters.map((f) => (
                <li key={f.id}>
                  <FilterButton
                    id={f.id}
                    label={f.label}
                    IconComp={f.icon}
                    isActive={activeFilter === f.id}
                    count={counts[f.id] ?? 0}
                    onClick={(id) => handleFilterClick(id)}
                  />
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg font-bold text-white">Мои проекты</h3>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-white"
                    onClick={startAddProject}
                    aria-label="Создать новый проект"
                  >
                    <Plus size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Создать новый проект</p>
                </TooltipContent>
              </Tooltip>
            </div>

            <nav aria-label="Проекты пользователя">
              <ul className="flex flex-col gap-1">
                {projects.map((p) => (
                  <ProjectRow
                    key={p.id}
                    project={p}
                    isActive={activeFilter === p.id}
                    count={projectCounts[p.id] ?? 0}
                    isEditing={editingProject?.id === p.id}
                    editValue={newProjectName}
                    onEditValueChange={setNewProjectName}
                    onSelect={handleSelectProject}
                    onStartEdit={handleStartEdit}
                    onSave={handleSaveEdit}
                    onDelete={handleDeleteProject}
                  />
                ))}

                {isAddingProject && (
                  <li className="flex items-center gap-2 w-full p-2">
                    <Input
                      value={addProjectName}
                      onChange={(e) => setAddProjectName(e.target.value)}
                      onBlur={handleCreateProject}
                      onKeyDown={(e) => e.key === 'Enter' && handleCreateProject()}
                      className="h-7"
                      placeholder="Название проекта..."
                      autoFocus
                      disabled={isSubmitting}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={handleCreateProject}
                      className="h-7 w-7"
                      aria-label="Сохранить проект"
                      disabled={isSubmitting}
                    >
                      <Check size={16} />
                    </Button>
                  </li>
                )}
              </ul>
            </nav>
          </div>
        </div>
      </aside>
    </TooltipProvider>
  );
}
